/* Everything you owe someone.
 *
 * One thing, deliberately. Tasks are work, and the promise of the product is
 * that you should not have to decide when work happens — so this is a database
 * rather than a list: properties in columns, grouped and sorted the way you
 * actually work. See tasks/TaskTable.tsx for why every column here is a field
 * the solver genuinely reads.
 *
 * Appointments used to sit above this, and no longer do. They belong to the
 * calendar: a thing with a time of its own is something you look at *on a
 * day*, and putting it at the top of the backlog meant reading the same
 * appointment twice — once here and once where it actually happens. Adding one
 * is now the same dialog as adding anything else to a day (ui/AddToCalendar),
 * and the appointment's own screen is the day it is on.
 */

import { useSearchParams } from 'react-router-dom';
import Icon from '../ui/Icon';
import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ActivityType, Task, Urgency } from '@shared/types';
import { get, post, errorMessage } from '../api';
import { useSession, localToday } from '../session';
import { useToast } from '../ui/toast';
import TaskTable, { URGENCY_LABEL } from './tasks/TaskTable';

export default function TasksScreen() {
  /* Arriving from New → A task: the cursor is already where the typing goes. */
  const focusAdd = useSearchParams()[0].get('add') === '1';
  const { loading, today, user } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const realToday = today ?? localToday(user?.tz);

  const tasks = useQuery({
    queryKey: ['tasks'],
    enabled: !loading,
    queryFn: async () => {
      const r = await get<{ tasks: Task[] }>('/api/tasks');
      if (!r.ok) throw new Error(errorMessage(r, 'Could not load your tasks.'));
      return r.tasks;
    }
  });

  /* The Type column needs the categories, and they are the same list the
     Balance screen edits — one query key, so adding a hobby there fills the
     dropdown here without a reload. */
  const types = useQuery({
    queryKey: ['activity-types'],
    enabled: !loading,
    queryFn: async () => {
      const r = await get<{ types: ActivityType[] }>('/api/activity-types');
      return r.ok ? r.types : [];
    }
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['tasks'] });
    void qc.invalidateQueries({ queryKey: ['plan'] });
    void qc.invalidateQueries({ queryKey: ['balance'] });
    void qc.invalidateQueries({ queryKey: ['rewards'] });
    /* An appointment writes a task as well — the getting-ready one — and a
       task can be the only reason an appointment row changed. Refreshing both
       from one place is what keeps the two halves of this screen agreeing. */
  };

  const all = tasks.data ?? [];
  const open = useMemo(() => all.filter(t => !t.done), [all]);
  const unplanned = open.filter(t => !t.placedAt).length;

  return (
    <div className="screen">
      <header className="topbar">
        <div>
          <h1>Tasks</h1>
          <p className="muted">
            {open.length} open
            {unplanned > 0 && ` · ${unplanned} not on a day yet`}
          </p>
        </div>
      </header>

      <NewTask types={types.data ?? []} autoFocus={focusAdd}
               onAdded={t => { refresh(); toast.say(`Added “${t}”.`, 'good'); }} />

      {(loading || tasks.isPending) && !tasks.isError && (
        <ol className="tasklist" aria-busy="true" aria-label="Loading your tasks">
          {[0, 1, 2].map(i => <li key={i} className="task task-skeleton" />)}
        </ol>
      )}

      {tasks.isError && (
        <div className="notice notice-bad" role="alert">
          <p><strong>Could not load your tasks.</strong></p>
          <p className="muted small">{(tasks.error as Error).message}</p>
          <button className="btn btn-ghost btn-sm" onClick={() => tasks.refetch()}>Try again</button>
        </div>
      )}

      {tasks.data && (
        <TaskTable tasks={all} types={types.data ?? []} today={realToday} onChanged={refresh} />
      )}
    </div>
  );
}

/* ── capture, which has to stay one line ─────────────────────────
   Everything else on this screen is for organising what is already written
   down. This is the part that has to work while you are on the phone to
   somebody, so it stays a single row: what, how long, how urgent, done. */
function NewTask({ types, onAdded, autoFocus }: {
  autoFocus?: boolean;
  types: ActivityType[];
  onAdded: (title: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [mins, setMins] = useState('30');
  const [urgency, setUrgency] = useState<Urgency>('should');
  const [typeId, setTypeId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: async () => {
      const r = await post('/api/tasks', {
        title: title.trim(),
        estMin: Math.max(5, Math.min(600, Number(mins) || 30)),
        urgency,
        typeId: typeId || null
      });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not add that.'));
      return title.trim();
    },
    onSuccess: t => { onAdded(t); setTitle(''); setError(null); },
    onError: (e: Error) => setError(e.message)
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) { setError('What is it?'); return; }
    add.mutate();
  }

  return (
    <form className="quickadd task-add" onSubmit={submit} aria-label="Add a task">
      <span className="task-add-icon" aria-hidden="true"><Icon name="plus" size={18} /></span>
      <input value={title} maxLength={200} placeholder="What do you owe someone?"
             autoFocus={autoFocus}
             aria-label="What do you owe someone?"
             onChange={e => setTitle(e.target.value)} />
      <span className="task-add-mins">
        <Icon name="clock" size={14} />
        <input type="number" min="5" max="600" step="5" aria-label="Minutes"
               value={mins} onChange={e => setMins(e.target.value)} />
        <span className="muted small" aria-hidden="true">min</span>
      </span>
      <select value={urgency} aria-label="Urgency"
              onChange={e => setUrgency(e.target.value as Urgency)}>
        {(Object.keys(URGENCY_LABEL) as Urgency[]).map(u =>
          <option key={u} value={u}>{URGENCY_LABEL[u]}</option>)}
      </select>
      {types.length > 0 && (
        <select value={typeId} aria-label="Type"
                onChange={e => setTypeId(e.target.value)}>
          <option value="">No type</option>
          {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      )}
      <button className="btn btn-primary btn-sm" type="submit" disabled={add.isPending}>
        <Icon name="plus" size={16} />{add.isPending ? 'Adding…' : 'Add'}
      </button>
      {error && <p className="auth-err" role="alert">{error}</p>}
    </form>
  );
}
